import { Link, useParams } from "react-router-dom";
import { SiteHeader, PlatformFooter } from "../platform/PlatformChrome";
import { BlockRenderer } from "../editor/BlockRenderer";
import { lessonEditorConfig } from "../editor/courseConfig";
import { useCourse, useCourseProgress } from "../../lib/courses";
import { useMe } from "../../lib/auth";
import type { PublicCourse, PublicCourseItem, PublicCourseProgress } from "@vedaunion/shared";

/**
 * Course overview / syllabus — `/courses/:slug` (public) and the dashboard
 * equivalent. A functional product surface: compact header with title, level,
 * total time and a prominent Start / Resume button; a progress bar when logged
 * in; the overview body in a constrained reading column; and a scannable
 * ordered syllabus of lessons + exercise sets with completion ticks.
 */

const LEVEL_LABEL: Record<PublicCourse["level"], string> = {
  beginner: "Beginner",
  intermediate: "Intermediate",
  advanced: "Advanced",
};

export default function CoursePage({ surface }: { surface: "public" | "dashboard" }) {
  const { slug } = useParams<{ slug: string }>();

  const body = !slug ? (
    <div className="course-shell">
      <p className="course-meta">No course specified.</p>
    </div>
  ) : (
    <CourseBody slug={slug} />
  );

  if (surface === "public") {
    return (
      <>
        <SiteHeader />
        <main className="course-surface pt-24 sm:pt-28 pb-16">{body}</main>
        <PlatformFooter />
      </>
    );
  }
  return <div className="course-surface py-2">{body}</div>;
}

function CourseBody({ slug }: { slug: string }) {
  const me = useMe();
  const user = me.data?.user ?? null;
  const courseQ = useCourse(slug);
  const c = courseQ.data?.course;
  const progressQ = useCourseProgress(slug, !!user);
  const progress = progressQ.data?.progress ?? null;

  if (courseQ.isLoading) {
    return (
      <div className="course-shell">
        <p className="course-meta">Loading…</p>
      </div>
    );
  }
  if (!c) {
    return (
      <div className="course-shell">
        <h1 className="course-h2 mb-3">Course not found.</h1>
        <Link to="/courses" className="course-back">
          ← Back to courses
        </Link>
      </div>
    );
  }

  const completedLessonIds = new Set(
    (progress?.lessons ?? [])
      .filter((l) => l.status === "completed")
      .map((l) => l.lessonId),
  );

  const startItem = pickStartItem(c.items, completedLessonIds, progress);
  const startHref = startItem ? itemHref(slug, startItem) : null;
  const started = !!progress && progress.completedItemCount > 0;
  const allDone = !!progress && progress.totalItemCount > 0 && progress.percent >= 100;
  const startLabel = allDone ? "Review course" : started ? "Resume" : "Start course";

  const lessonCount = c.items.filter((i) => i.kind === "lesson").length;
  const quizCount = c.items.filter((i) => i.kind === "quiz").length;
  const headerMeta = [
    LEVEL_LABEL[c.level],
    c.estimatedMinutes ? `${c.estimatedMinutes} min` : null,
    lessonCount ? `${lessonCount} lesson${lessonCount === 1 ? "" : "s"}` : null,
    quizCount ? `${quizCount} exercise set${quizCount === 1 ? "" : "s"}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="course-shell">
      <Link to="/courses" className="course-back mb-5">
        ← Courses
      </Link>

      <header className="course-measure">
        <p className="course-eyebrow course-eyebrow-violet mb-2">{LEVEL_LABEL[c.level]}</p>
        <h1 className="course-title">{c.title}</h1>
        {c.subtitle && <p className="course-subtitle mt-3">{c.subtitle}</p>}
        {headerMeta && <p className="course-meta mt-3">{headerMeta}</p>}

        {/* Start / Resume + progress */}
        <div className="mt-5 flex flex-col gap-4">
          {startHref && (
            <Link to={startHref} className="course-cta self-start">
              {startLabel}
              <span aria-hidden>→</span>
            </Link>
          )}
          {user && progress && progress.totalItemCount > 0 && (
            <div className="max-w-[420px]">
              <div className="flex items-center justify-between course-meta mb-1.5">
                <span>
                  {progress.completedItemCount} / {progress.totalItemCount} complete
                </span>
                <span className="tabular">{progress.percent}%</span>
              </div>
              <div className="course-progress-track">
                <div className="course-progress-fill" style={{ width: `${progress.percent}%` }} />
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Overview body */}
      <div className="course-measure mt-8 lesson-prose">
        <BlockRenderer doc={c.contentJson} config={lessonEditorConfig} htmlFallback={c.contentHtml} />
      </div>

      {/* Syllabus */}
      {c.items.length > 0 && (
        <section className="course-measure mt-9 pt-7 border-t border-[var(--color-rule)]">
          <p className="course-eyebrow course-eyebrow-violet mb-1">Syllabus</p>
          <h2 className="course-h2 mb-4">What you'll cover</h2>
          <ol className="course-syllabus">
            {c.items.map((item, idx) => (
              <SyllabusRow
                key={item.id}
                slug={slug}
                item={item}
                index={idx}
                completed={
                  item.kind === "lesson" && item.lesson
                    ? completedLessonIds.has(item.lesson.id)
                    : false
                }
              />
            ))}
          </ol>
        </section>
      )}
    </div>
  );
}

function SyllabusRow({
  slug,
  item,
  index,
  completed,
}: {
  slug: string;
  item: PublicCourseItem;
  index: number;
  completed: boolean;
}) {
  const href = itemHref(slug, item);
  if (!href) return null;

  const isLesson = item.kind === "lesson";
  const title = (isLesson ? item.lesson?.title : item.quiz?.title) ?? "Untitled";
  const kindLabel = isLesson ? "Lesson" : "Exercise set";
  const meta = isLesson
    ? [
        item.lesson?.estimatedMinutes ? `${item.lesson.estimatedMinutes} min` : null,
        item.lesson?.materialCount
          ? `${item.lesson.materialCount} material${item.lesson.materialCount === 1 ? "" : "s"}`
          : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : [
        item.quiz?.questionCount
          ? `${item.quiz.questionCount} question${item.quiz.questionCount === 1 ? "" : "s"}`
          : null,
        item.quiz ? `Pass ${item.quiz.passThreshold}%` : null,
      ]
        .filter(Boolean)
        .join(" · ");

  return (
    <li>
      <Link to={href} className="course-syllabus-row group">
        <span
          className={`course-index ${completed ? "is-done" : ""}`}
          aria-hidden
        >
          {completed ? "✓" : index + 1}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block course-eyebrow mb-0.5">{kindLabel}</span>
          <span className="block course-row-title line-clamp-2">{title}</span>
          {meta && <span className="block course-meta mt-0.5">{meta}</span>}
        </span>
        <span
          aria-hidden
          className="flex-none text-[var(--color-ink-light)] group-hover:text-[var(--color-violet)] transition-colors pr-1"
        >
          →
        </span>
      </Link>
    </li>
  );
}

function itemHref(slug: string, item: PublicCourseItem): string | null {
  if (item.kind === "lesson" && item.lesson) {
    return `/courses/${slug}/lessons/${item.lesson.id}`;
  }
  if (item.kind === "quiz" && item.quiz) {
    return `/courses/${slug}/quizzes/${item.quiz.id}`;
  }
  return null;
}

/** First incomplete item, falling back to the first item. */
function pickStartItem(
  items: PublicCourseItem[],
  completedLessonIds: Set<number>,
  progress: PublicCourseProgress | null,
): PublicCourseItem | null {
  if (items.length === 0) return null;
  for (const item of items) {
    if (item.kind === "lesson" && item.lesson) {
      if (!completedLessonIds.has(item.lesson.id)) return item;
    } else if (!progress || progress.completedItemCount < items.length) {
      return item;
    }
  }
  return items[0]!;
}
